import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Text, useTheme } from '@Sage/hoi-poi-ui';
import { downloadFile, sanitizeFilename } from '@Sage/ssm-utils';

import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import CampaignPreviewTestModal from 'containers/campaigns/marketing/MarketingCampaignsForm/CampaignPreviewTestModal';
import SaveAsTemplateModal from 'containers/campaigns/marketing/components/SaveAsTemplateModal';
import { applyFixedFooterPreviewToHtml } from 'containers/campaigns/marketing/EmailEditor/fixedFooter';
import { formatMarketingSenderPostalAddress } from 'containers/campaigns/marketing/settings/unsubscribe/templates';
import { extractTopolJsonFromMetadata } from 'containers/campaigns/marketing/utils/marketingCampaignMetadata';
import Context from 'managers/Context';
import { createMarketingEmailTemplate } from 'services/CampaignsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';

import { buildCampaignDetailsMetaParts } from './campaignDetailsMeta';

const showGenericBackendErrorToast = () => {
    errorToast({ text: getLiteral('error_generalerror') });
};

const CampaignDetailsHeader = memo(({ campaign }) => {
    const theme = useTheme();
    const labelMuted = theme.colors.textLight.secondary;
    const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
    const [isSaveAsTemplateModalOpen, setIsSaveAsTemplateModalOpen] = useState(false);
    const [isSaveAsTemplateSubmitting, setIsSaveAsTemplateSubmitting] = useState(false);

    const html = typeof campaign?.body === 'string' ? campaign.body : '';
    const name = typeof campaign?.name === 'string' ? campaign.name : '';
    const subject = typeof campaign?.subject === 'string' ? campaign.subject : '';
    const previewText = typeof campaign?.previewText === 'string' ? campaign.previewText : '';
    const metaParts = useMemo(() => buildCampaignDetailsMetaParts(campaign ?? {}), [campaign]);
    const senderEmail = campaign?.senderId?.email || '';
    const senderName = campaign?.senderId?.label || '';
    const senderReplyEmail = campaign?.senderId?.replyEmail || '';
    const senderAddressFromCampaign =
        campaign?.senderAddress == null ? '' : String(campaign.senderAddress).trim();
    const senderAddressFallback = formatMarketingSenderPostalAddress(campaign?.senderId);
    const footerPreview = useMemo(
        () => ({
            senderName: String(campaign?.senderId?.label ?? campaign?.senderId?.name ?? '').trim(),
            senderAddress: (senderAddressFallback || senderAddressFromCampaign || '').trim(),
            footerHere: String(
                campaign?.footerHere ?? getLiteral('label_marketing_campaigns_unsubscribe_here'),
            ).trim(),
            linkHref: '#',
        }),
        [
            campaign?.footerHere,
            campaign?.senderId,
            senderAddressFallback,
            senderAddressFromCampaign,
        ],
    );
    const previewHtml = useMemo(
        () => applyFixedFooterPreviewToHtml(html, footerPreview),
        [footerPreview, html],
    );
    const hasBody = Boolean(previewHtml?.trim?.());
    const campaignTopolJson = useMemo(
        () => extractTopolJsonFromMetadata(campaign?.metadata),
        [campaign?.metadata],
    );

    const loggedUserEmail = useMemo(
        () => Context.store.getState()?.config?.userData?.email || '',
        [],
    );

    const handleOpenPreviewModal = useCallback(() => {
        setPreviewModalOpen(true);
    }, []);

    const handleClosePreviewModal = useCallback(() => {
        setPreviewModalOpen(false);
    }, []);

    const handleOpenSaveAsTemplateModal = useCallback(() => {
        setIsSaveAsTemplateModalOpen(true);
    }, []);

    const handleCloseSaveAsTemplateModal = useCallback(() => {
        if (isSaveAsTemplateSubmitting) {
            return;
        }
        setIsSaveAsTemplateModalOpen(false);
    }, [isSaveAsTemplateSubmitting]);

    const handleConfirmSaveAsTemplate = useCallback(
        async (templateName) => {
            if (isSaveAsTemplateSubmitting) {
                return;
            }

            const payload = {
                name: templateName,
                html: html || '',
                ...(campaignTopolJson ? { topolConfig: campaignTopolJson } : {}),
            };

            setIsSaveAsTemplateSubmitting(true);

            try {
                await createMarketingEmailTemplate(payload);
                successToast({
                    title: getLiteral('label_marketing_campaigns_design_save_as_template'),
                    text: getLiteral('label_marketing_campaigns_design_save_as_template_success'),
                });
                setIsSaveAsTemplateModalOpen(false);
            } catch {
                showGenericBackendErrorToast();
            } finally {
                setIsSaveAsTemplateSubmitting(false);
            }
        },
        [campaignTopolJson, html, isSaveAsTemplateSubmitting],
    );

    const handleDownloadHtml = useCallback(() => {
        const filename = sanitizeFilename(name || 'campaign');
        downloadFile(previewHtml, `${filename}.html`, 'text/html');
    }, [name, previewHtml]);

    const menuItems = useMemo(
        () => [
            {
                title: getLiteral('label_marketing_campaigns_design_save_as_template'),
                onClick: handleOpenSaveAsTemplateModal,
            },
            {
                title: getLiteral('label_marketing_campaigns_design_download_html'),
                onClick: handleDownloadHtml,
            },
        ],
        [handleDownloadHtml, handleOpenSaveAsTemplateModal],
    );

    const previewActionLabel = getLiteral('label_marketing_campaigns_view_email');
    return (
        <header className="fm-campaign-details__header">
            <div className="fm-campaign-details__header-top">
                <div className="fm-campaign-details__thumb-wrap">
                    {hasBody ? (
                        <div className="fm-campaign-details__thumb-surface">
                            <HtmlPreviewFrame
                                className="fm-campaign-details__thumb-iframe"
                                html={previewHtml}
                                title={campaign.name}
                                scale={0.32}
                                actionLabel={previewActionLabel}
                                actionIcon="searchRaw"
                                isPreviewOpen={isPreviewModalOpen}
                                onClickAction={handleOpenPreviewModal}
                                menuItems={menuItems}
                            />
                        </div>
                    ) : (
                        <div
                            className="fm-campaign-details__thumb-placeholder"
                            aria-hidden={true}
                        />
                    )}
                </div>
                <div className="fm-campaign-details__header-text">
                    <div className="fm-campaign-details__header-lead">
                        {name ? (
                            <Text type="h6" bold className="fm-campaign-details__title">
                                {name}
                            </Text>
                        ) : null}
                        {metaParts.idPart || metaParts.detail ? (
                            <div className="fm-campaign-details__meta-line">
                                {metaParts.idPart ? (
                                    <Text type="subtitle" medium>
                                        {metaParts.idPart}
                                    </Text>
                                ) : null}
                                {metaParts.idPart && metaParts.detail ? (
                                    <span className="fm-campaign-details__meta-line-separator">
                                        •
                                    </span>
                                ) : null}
                                {metaParts.detail ? (
                                    <Text type="caption" color={labelMuted}>
                                        {metaParts.detail}
                                    </Text>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="fm-campaign-details__fields">
                        <div className="fm-campaign-details__field">
                            <Text
                                className="fm-campaign-details__field-label"
                                type="caption"
                                color={labelMuted}
                            >
                                {getLiteral('label_subject')}
                            </Text>
                            <Text className="fm-campaign-details__field-value" size="small">
                                {subject || '—'}
                            </Text>
                        </div>
                        <div className="fm-campaign-details__field">
                            <Text
                                className="fm-campaign-details__field-label"
                                type="caption"
                                color={labelMuted}
                            >
                                {getLiteral('label_from')}
                            </Text>
                            {senderName || senderEmail ? (
                                <div className="fm-campaign-details__field-value-stack">
                                    {senderName ? (
                                        <Text
                                            className="fm-campaign-details__field-value"
                                            size="small"
                                        >
                                            {senderName}
                                        </Text>
                                    ) : null}
                                    {senderEmail ? (
                                        <Text
                                            className="fm-campaign-details__field-value"
                                            size="small"
                                        >
                                            {senderEmail}
                                        </Text>
                                    ) : null}
                                </div>
                            ) : (
                                <Text className="fm-campaign-details__field-value" size="small">
                                    —
                                </Text>
                            )}
                        </div>
                        <div className="fm-campaign-details__field">
                            <Text
                                className="fm-campaign-details__field-label"
                                type="caption"
                                color={labelMuted}
                            >
                                {getLiteral('label_reply_to')}
                            </Text>
                            {senderReplyEmail ? (
                                <div className="fm-campaign-details__field-value-stack">
                                    <Text className="fm-campaign-details__field-value" size="small">
                                        {senderReplyEmail}
                                    </Text>
                                </div>
                            ) : (
                                <Text className="fm-campaign-details__field-value" size="small">
                                    —
                                </Text>
                            )}
                        </div>
                    </div>
                    {hasBody ? (
                        <div className="fm-campaign-details__view-email-wrap">
                            <Button
                                type="secondary"
                                icon="visibilityOn"
                                className="fm-campaign-details__view-email"
                                onClick={handleOpenPreviewModal}
                            >
                                {previewActionLabel}
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
            <CampaignPreviewTestModal
                isOpen={isPreviewModalOpen}
                onRequestClose={handleClosePreviewModal}
                from={senderEmail || ''}
                subject={subject}
                sendAt={campaign.launchAt?.time}
                previewText={previewText}
                htmlPreview={previewHtml}
                defaultRecipient={loggedUserEmail}
                withoutSend
            />
            <SaveAsTemplateModal
                isOpen={isSaveAsTemplateModalOpen}
                isSubmitting={isSaveAsTemplateSubmitting}
                defaultName=""
                onCancel={handleCloseSaveAsTemplateModal}
                onRequestClose={handleCloseSaveAsTemplateModal}
                onConfirm={handleConfirmSaveAsTemplate}
            />
        </header>
    );
});

CampaignDetailsHeader.displayName = 'CampaignDetailsHeader';

CampaignDetailsHeader.propTypes = {
    campaign: PropTypes.object,
};

export default CampaignDetailsHeader;
